import Dexie, { type Table } from "dexie";

import type {
    AnyContributionEventEnvelopeV1,
    ContributionCheckpointV1,
    ContributionEpisodeAbandonPayloadV1,
    ContributionEpisodeCompletePayloadV1,
    ContributionEpisodeCreatePayloadV1,
} from "./types";

export interface ContributionProducerIdentity {
    producerId: string;
    producerSeq: number;
}

export interface StoredContributionEpisode {
    ownerUid: string;
    clientEpisodeId: string;
    sessionId: string;
    status: "pending" | "active" | "completing" | "abandoning" | "abandoned" | "completed" | "blocked";
    createPayload: ContributionEpisodeCreatePayloadV1;
    authorityKey?: string;
    serverEpisodeVersion?: string;
    completePayload?: ContributionEpisodeCompletePayloadV1;
    abandonPayload?: ContributionEpisodeAbandonPayloadV1;
    createdAtMs: number;
    updatedAtMs: number;
    nextAttemptAtMs: number;
    attemptCount: number;
    blockedAtMs?: number;
    blockedReasonCode?: string;
}

export interface StoredContributionEvent {
    ownerUid: string;
    eventId: string;
    clientEpisodeId: string;
    sessionId: string;
    producerId: string;
    producerSeq: number;
    eventType: string;
    createdAtMs: number;
    nextAttemptAtMs: number;
    attemptCount: number;
    lastErrorCode?: string;
    envelope: AnyContributionEventEnvelopeV1;
}

export interface StoredContributionCheckpoint {
    ownerUid: string;
    checkpointId: string;
    clientEpisodeId: string;
    sessionId: string;
    createdAtMs: number;
    nextAttemptAtMs: number;
    attemptCount: number;
    lastErrorCode?: string;
    checkpoint: ContributionCheckpointV1;
}

interface ContributionMetaRow {
    key: string;
    producerId: string;
    nextSequence: number;
}

export interface QuarantinedContributionItem {
    ownerUid: string;
    quarantineId: string;
    sourceId: string;
    clientEpisodeId: string;
    kind: "event" | "checkpoint";
    reasonCode: string;
    quarantinedAtMs: number;
    item: AnyContributionEventEnvelopeV1 | ContributionCheckpointV1;
}

export interface AppendContributionEventResult {
    enqueued: boolean;
    queueDepth: number;
    event?: AnyContributionEventEnvelopeV1;
    reason?: "capacity" | "missing_episode";
}

export interface ContributionPendingCounts {
    events: number;
    checkpoints: number;
}

export interface ContributionCaptureStore {
    createEpisode(ownerUid: string, payload: ContributionEpisodeCreatePayloadV1, authorityKey: string): Promise<void>;
    appendEvent(
        ownerUid: string,
        clientEpisodeId: string,
        producerId: string,
        maxQueueItems: number,
        build: (identity: ContributionProducerIdentity) => AnyContributionEventEnvelopeV1,
    ): Promise<AppendContributionEventResult>;
    addCheckpoint(ownerUid: string, checkpoint: ContributionCheckpointV1, maxQueueItems: number): Promise<boolean>;
    requestEpisodeComplete(
        ownerUid: string,
        clientEpisodeId: string,
        payload: ContributionEpisodeCompletePayloadV1,
    ): Promise<void>;
    requestAuthorityAbandon(
        ownerUid: string,
        authorityKey: string,
        payload: ContributionEpisodeAbandonPayloadV1,
    ): Promise<number>;
    requestUnauthorizedEpisodesAbandon(
        ownerUid: string,
        currentAuthorityKey: string | null,
        payload: ContributionEpisodeAbandonPayloadV1,
    ): Promise<number>;
    listAbandoningEpisodes(ownerUid: string, limit: number): Promise<StoredContributionEpisode[]>;
    markEpisodeAbandoned(ownerUid: string, clientEpisodeId: string): Promise<void>;
    listOpenEpisodes(ownerUid: string, limit: number): Promise<StoredContributionEpisode[]>;
    setEpisodeVersion(ownerUid: string, clientEpisodeId: string, serverEpisodeVersion: string): Promise<void>;
    markEpisodeRetry(ownerUid: string, clientEpisodeId: string, nextAttemptAtMs: number): Promise<void>;
    markEpisodeBlocked(ownerUid: string, clientEpisodeId: string, errorCode: string): Promise<void>;
    getReadyEvents(ownerUid: string, clientEpisodeId: string, limit: number, nowMs: number): Promise<StoredContributionEvent[]>;
    removeEvents(ownerUid: string, eventIds: string[]): Promise<void>;
    quarantineEvents(ownerUid: string, rejections: Array<{ id: string; reasonCode: string }>): Promise<void>;
    markEventsRetry(ownerUid: string, eventIds: string[], nextAttemptAtMs: number, errorCode: string): Promise<void>;
    getReadyCheckpoints(
        ownerUid: string,
        clientEpisodeId: string,
        limit: number,
        nowMs: number,
    ): Promise<StoredContributionCheckpoint[]>;
    removeCheckpoints(ownerUid: string, checkpointIds: string[]): Promise<void>;
    quarantineCheckpoints(ownerUid: string, rejections: Array<{ id: string; reasonCode: string }>): Promise<void>;
    markCheckpointsRetry(
        ownerUid: string,
        checkpointIds: string[],
        nextAttemptAtMs: number,
        errorCode: string,
    ): Promise<void>;
    getPendingCounts(ownerUid: string, clientEpisodeId: string): Promise<ContributionPendingCounts>;
    markEpisodeCompleted(ownerUid: string, clientEpisodeId: string): Promise<void>;
    getQueueDepth(ownerUid: string): Promise<number>;
    getQuarantineCount(ownerUid: string): Promise<number>;
}

class ContributionCaptureDB extends Dexie {
    episodes!: Table<StoredContributionEpisode, string>;
    events!: Table<StoredContributionEvent, string>;
    checkpoints!: Table<StoredContributionCheckpoint, string>;
    meta!: Table<ContributionMetaRow, string>;
    quarantine!: Table<QuarantinedContributionItem, string>;

    constructor(dbName = "LibrettoContributionCaptureDB") {
        super(dbName);
        this.version(1).stores({
            episodes: "&clientEpisodeId, status, createdAtMs, nextAttemptAtMs",
            events: "&eventId, clientEpisodeId, sessionId, [producerId+producerSeq], eventType, createdAtMs, nextAttemptAtMs",
            checkpoints: "&checkpointId, clientEpisodeId, sessionId, createdAtMs, nextAttemptAtMs",
            meta: "&key",
        });
        this.version(2).stores({
            quarantine: "&quarantineId, sourceId, clientEpisodeId, kind, reasonCode, quarantinedAtMs",
        });
        this.version(3).stores({
            episodes: "&clientEpisodeId, ownerUid, [ownerUid+status], status, createdAtMs, nextAttemptAtMs",
            events: "&eventId, ownerUid, [ownerUid+clientEpisodeId], clientEpisodeId, sessionId, [producerId+producerSeq], eventType, createdAtMs, nextAttemptAtMs",
            checkpoints: "&checkpointId, ownerUid, [ownerUid+clientEpisodeId], clientEpisodeId, sessionId, createdAtMs, nextAttemptAtMs",
            quarantine: "&quarantineId, ownerUid, [ownerUid+clientEpisodeId], sourceId, clientEpisodeId, kind, reasonCode, quarantinedAtMs",
        }).upgrade(async (transaction) => {
            // V1/V2 rows predate authenticated ownership. Keep them for local
            // diagnostics, but give them an empty owner so no authenticated UID
            // can ever select or transmit them.
            await Promise.all([
                transaction.table("episodes").toCollection().modify((row: any) => {
                    if (typeof row.ownerUid !== "string") row.ownerUid = "";
                }),
                transaction.table("events").toCollection().modify((row: any) => {
                    if (typeof row.ownerUid !== "string") row.ownerUid = "";
                }),
                transaction.table("checkpoints").toCollection().modify((row: any) => {
                    if (typeof row.ownerUid !== "string") row.ownerUid = "";
                }),
                transaction.table("quarantine").toCollection().modify((row: any) => {
                    if (typeof row.ownerUid !== "string") row.ownerUid = "";
                }),
            ]);
        });
    }
}

export class IndexedDbContributionCaptureStore implements ContributionCaptureStore {
    private readonly db: ContributionCaptureDB;

    constructor(dbName = "LibrettoContributionCaptureDB") {
        this.db = new ContributionCaptureDB(dbName);
    }

    private assertOwnerUid(ownerUid: string): void {
        if (!String(ownerUid ?? "").trim()) throw new Error("capture owner is required");
    }

    private async requireOwnedEpisode(
        ownerUid: string,
        clientEpisodeId: string,
    ): Promise<StoredContributionEpisode> {
        const episode = await this.db.episodes.get(clientEpisodeId);
        if (!episode || episode.ownerUid !== ownerUid) throw new Error("capture episode owner mismatch");
        return episode;
    }

    private async ownerQueueDepth(ownerUid: string): Promise<number> {
        const [events, checkpoints] = await Promise.all([
            this.db.events.where("ownerUid").equals(ownerUid).count(),
            this.db.checkpoints.where("ownerUid").equals(ownerUid).count(),
        ]);
        return events + checkpoints;
    }

    private quarantineId(ownerUid: string, kind: "event" | "checkpoint", sourceId: string): string {
        return `${ownerUid}:${kind}:${sourceId}`;
    }

    async createEpisode(
        ownerUid: string,
        payload: ContributionEpisodeCreatePayloadV1,
        authorityKey: string,
    ): Promise<void> {
        this.assertOwnerUid(ownerUid);
        const now = Date.now();
        await this.db.transaction("rw", this.db.episodes, async () => {
            const existing = await this.db.episodes.get(payload.clientEpisodeId);
            if (existing) {
                if (existing.ownerUid !== ownerUid) throw new Error("capture episode owner mismatch");
                return;
            }
            await this.db.episodes.add({
                ownerUid,
                clientEpisodeId: payload.clientEpisodeId,
                sessionId: payload.sessionId,
                status: "pending",
                createPayload: payload,
                authorityKey,
                createdAtMs: now,
                updatedAtMs: now,
                nextAttemptAtMs: 0,
                attemptCount: 0,
            });
        });
    }

    async appendEvent(
        ownerUid: string,
        clientEpisodeId: string,
        producerId: string,
        maxQueueItems: number,
        build: (identity: ContributionProducerIdentity) => AnyContributionEventEnvelopeV1,
    ): Promise<AppendContributionEventResult> {
        this.assertOwnerUid(ownerUid);
        return this.db.transaction(
            "rw",
            this.db.episodes,
            this.db.events,
            this.db.checkpoints,
            this.db.meta,
            async () => {
                const episode = await this.db.episodes.get(clientEpisodeId);
                const queueDepth = await this.ownerQueueDepth(ownerUid);
                if (!episode || episode.ownerUid !== ownerUid ||
                    (episode.status !== "pending" && episode.status !== "active")) {
                    return { enqueued: false, queueDepth, reason: "missing_episode" };
                }
                if (queueDepth >= maxQueueItems) {
                    return { enqueued: false, queueDepth, reason: "capacity" };
                }

                const producerKey = `producer:${producerId}`;
                let producer = await this.db.meta.get(producerKey);
                if (!producer) {
                    producer = { key: producerKey, producerId, nextSequence: 1 };
                }
                const identity = {
                    producerId: producer.producerId,
                    producerSeq: producer.nextSequence,
                };
                const event = build(identity);
                if (event.clientEpisodeId !== clientEpisodeId) {
                    throw new Error("capture event episode mismatch");
                }
                const now = Date.now();
                await this.db.events.add({
                    ownerUid,
                    eventId: event.eventId,
                    clientEpisodeId: event.clientEpisodeId,
                    sessionId: event.sessionId,
                    producerId: event.producerId,
                    producerSeq: event.producerSeq,
                    eventType: event.eventType,
                    createdAtMs: now,
                    nextAttemptAtMs: 0,
                    attemptCount: 0,
                    envelope: event,
                });
                await this.db.meta.put({ ...producer, nextSequence: producer.nextSequence + 1 });
                return { enqueued: true, queueDepth: queueDepth + 1, event };
            },
        );
    }

    async addCheckpoint(
        ownerUid: string,
        checkpoint: ContributionCheckpointV1,
        maxQueueItems: number,
    ): Promise<boolean> {
        this.assertOwnerUid(ownerUid);
        return this.db.transaction(
            "rw",
            this.db.episodes,
            this.db.events,
            this.db.checkpoints,
            async () => {
                const episode = await this.db.episodes.get(checkpoint.clientEpisodeId);
                if (!episode || episode.ownerUid !== ownerUid ||
                    (episode.status !== "pending" && episode.status !== "active")) return false;
                const depth = await this.ownerQueueDepth(ownerUid);
                if (depth >= maxQueueItems) return false;
                await this.db.checkpoints.add({
                    ownerUid,
                    checkpointId: checkpoint.checkpointId,
                    clientEpisodeId: checkpoint.clientEpisodeId,
                    sessionId: checkpoint.sessionId,
                    createdAtMs: Date.now(),
                    nextAttemptAtMs: 0,
                    attemptCount: 0,
                    checkpoint,
                });
                return true;
            },
        );
    }

    async requestEpisodeComplete(
        ownerUid: string,
        clientEpisodeId: string,
        payload: ContributionEpisodeCompletePayloadV1,
    ): Promise<void> {
        this.assertOwnerUid(ownerUid);
        await this.db.transaction("rw", this.db.episodes, async () => {
            const episode = await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
            if (episode.status !== "pending" && episode.status !== "active") return;
            await this.db.episodes.update(clientEpisodeId, {
                status: "completing",
                completePayload: payload,
                updatedAtMs: Date.now(),
                nextAttemptAtMs: 0,
            });
        });
    }

    private async sweepEpisodesForAbandonment(
        ownerUid: string,
        shouldAbandon: (episode: StoredContributionEpisode) => boolean,
        payload: ContributionEpisodeAbandonPayloadV1,
    ): Promise<number> {
        this.assertOwnerUid(ownerUid);
        let swept = 0;
        await this.db.transaction(
            "rw",
            this.db.episodes,
            this.db.events,
            this.db.checkpoints,
            async () => {
                const episodes = await this.db.episodes.where("ownerUid").equals(ownerUid).toArray();
                const candidates = episodes.filter((episode) =>
                    (episode.status === "pending" || episode.status === "active" || episode.status === "completing") &&
                    shouldAbandon(episode)
                );
                const now = Date.now();
                for (const episode of candidates) {
                    const [events, checkpoints] = await Promise.all([
                        this.db.events.where("[ownerUid+clientEpisodeId]")
                            .equals([ownerUid, episode.clientEpisodeId]).primaryKeys(),
                        this.db.checkpoints.where("[ownerUid+clientEpisodeId]")
                            .equals([ownerUid, episode.clientEpisodeId]).primaryKeys(),
                    ]);
                    await Promise.all([
                        this.db.events.bulkDelete(events),
                        this.db.checkpoints.bulkDelete(checkpoints),
                        this.db.episodes.update(episode.clientEpisodeId, {
                            // Keep even never-created episodes in the control queue.
                            // The server reserves the deterministic client episode ID
                            // so an in-flight create cannot reopen capture after revoke.
                            status: "abandoning",
                            abandonPayload: payload,
                            updatedAtMs: now,
                            nextAttemptAtMs: 0,
                            attemptCount: 0,
                        }),
                    ]);
                    swept += 1;
                }
            },
        );
        return swept;
    }

    async requestAuthorityAbandon(
        ownerUid: string,
        authorityKey: string,
        payload: ContributionEpisodeAbandonPayloadV1,
    ): Promise<number> {
        return this.sweepEpisodesForAbandonment(
            ownerUid,
            (episode) => episode.authorityKey === authorityKey,
            payload,
        );
    }

    async requestUnauthorizedEpisodesAbandon(
        ownerUid: string,
        currentAuthorityKey: string | null,
        payload: ContributionEpisodeAbandonPayloadV1,
    ): Promise<number> {
        return this.sweepEpisodesForAbandonment(
            ownerUid,
            (episode) => currentAuthorityKey == null || episode.authorityKey !== currentAuthorityKey,
            payload,
        );
    }

    async listAbandoningEpisodes(ownerUid: string, limit: number): Promise<StoredContributionEpisode[]> {
        if (!String(ownerUid ?? "").trim()) return [];
        const rows = await this.db.episodes.where("[ownerUid+status]").equals([ownerUid, "abandoning"]).toArray();
        return rows
            .sort((a, b) => a.createdAtMs - b.createdAtMs)
            .slice(0, Math.max(1, limit));
    }

    async markEpisodeAbandoned(ownerUid: string, clientEpisodeId: string): Promise<void> {
        await this.db.transaction("rw", this.db.episodes, async () => {
            const episode = await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
            if (episode.status !== "abandoning" && episode.status !== "abandoned") return;
            await this.db.episodes.update(clientEpisodeId, {
                status: "abandoned",
                updatedAtMs: Date.now(),
                nextAttemptAtMs: 0,
                attemptCount: 0,
            });
        });
    }

    async listOpenEpisodes(ownerUid: string, limit: number): Promise<StoredContributionEpisode[]> {
        if (!String(ownerUid ?? "").trim()) return [];
        const rows = await this.db.episodes.where("ownerUid").equals(ownerUid).toArray();
        return rows
            .filter((row) => row.status === "pending" || row.status === "active" || row.status === "completing")
            .sort((a, b) => a.createdAtMs - b.createdAtMs)
            .slice(0, Math.max(1, limit));
    }

    async setEpisodeVersion(
        ownerUid: string,
        clientEpisodeId: string,
        serverEpisodeVersion: string,
    ): Promise<void> {
        await this.db.transaction("rw", this.db.episodes, async () => {
            const existing = await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
            if (existing.status === "abandoning") {
                await this.db.episodes.update(clientEpisodeId, {
                    serverEpisodeVersion,
                    updatedAtMs: Date.now(),
                    nextAttemptAtMs: 0,
                    attemptCount: 0,
                });
                return;
            }
            if (existing.status !== "pending" && existing.status !== "active" && existing.status !== "completing") return;
            await this.db.episodes.update(clientEpisodeId, {
                serverEpisodeVersion,
                status: existing.status === "completing" ? "completing" : "active",
                updatedAtMs: Date.now(),
                nextAttemptAtMs: 0,
                attemptCount: 0,
            });
        });
    }

    async markEpisodeRetry(
        ownerUid: string,
        clientEpisodeId: string,
        nextAttemptAtMs: number,
    ): Promise<void> {
        await this.db.transaction("rw", this.db.episodes, async () => {
            const episode = await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
            if (episode.status !== "pending" && episode.status !== "active" &&
                episode.status !== "completing" && episode.status !== "abandoning") return;
            await this.db.episodes.update(clientEpisodeId, {
                nextAttemptAtMs,
                attemptCount: episode.attemptCount + 1,
                updatedAtMs: Date.now(),
            });
        });
    }

    async markEpisodeBlocked(ownerUid: string, clientEpisodeId: string, errorCode: string): Promise<void> {
        this.assertOwnerUid(ownerUid);
        await this.db.transaction(
            "rw",
            this.db.episodes,
            this.db.events,
            this.db.checkpoints,
            this.db.quarantine,
            async () => {
                await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
                const [events, checkpoints] = await Promise.all([
                    this.db.events.where("[ownerUid+clientEpisodeId]").equals([ownerUid, clientEpisodeId]).toArray(),
                    this.db.checkpoints.where("[ownerUid+clientEpisodeId]").equals([ownerUid, clientEpisodeId]).toArray(),
                ]);
                const now = Date.now();
                for (const row of events) {
                    await this.db.quarantine.put({
                        ownerUid,
                        quarantineId: this.quarantineId(ownerUid, "event", row.eventId),
                        sourceId: row.eventId,
                        clientEpisodeId,
                        kind: "event",
                        reasonCode: errorCode,
                        quarantinedAtMs: now,
                        item: row.envelope,
                    });
                }
                for (const row of checkpoints) {
                    await this.db.quarantine.put({
                        ownerUid,
                        quarantineId: this.quarantineId(ownerUid, "checkpoint", row.checkpointId),
                        sourceId: row.checkpointId,
                        clientEpisodeId,
                        kind: "checkpoint",
                        reasonCode: errorCode,
                        quarantinedAtMs: now,
                        item: row.checkpoint,
                    });
                }
                await Promise.all([
                    this.db.events.bulkDelete(events.map((row) => row.eventId)),
                    this.db.checkpoints.bulkDelete(checkpoints.map((row) => row.checkpointId)),
                    this.db.episodes.update(clientEpisodeId, {
                        status: "blocked",
                        blockedAtMs: now,
                        blockedReasonCode: errorCode,
                        updatedAtMs: now,
                        nextAttemptAtMs: 0,
                    }),
                ]);
            },
        );
    }

    async getReadyEvents(
        ownerUid: string,
        clientEpisodeId: string,
        limit: number,
        nowMs: number,
    ): Promise<StoredContributionEvent[]> {
        if (!String(ownerUid ?? "").trim()) return [];
        const rows = await this.db.events
            .where("[ownerUid+clientEpisodeId]")
            .equals([ownerUid, clientEpisodeId])
            .toArray();
        return rows
            .filter((row) => row.nextAttemptAtMs <= nowMs)
            .sort((a, b) => a.producerSeq - b.producerSeq)
            .slice(0, Math.max(1, limit));
    }

    async removeEvents(ownerUid: string, eventIds: string[]): Promise<void> {
        if (eventIds.length === 0) return;
        await this.db.transaction("rw", this.db.events, async () => {
            for (const eventId of eventIds) {
                const row = await this.db.events.get(eventId);
                if (row?.ownerUid === ownerUid) await this.db.events.delete(eventId);
            }
        });
    }

    async quarantineEvents(
        ownerUid: string,
        rejections: Array<{ id: string; reasonCode: string }>,
    ): Promise<void> {
        if (rejections.length === 0) return;
        await this.db.transaction("rw", this.db.events, this.db.quarantine, async () => {
            for (const rejection of rejections) {
                const row = await this.db.events.get(rejection.id);
                if (!row || row.ownerUid !== ownerUid) continue;
                await this.db.quarantine.put({
                    ownerUid,
                    quarantineId: this.quarantineId(ownerUid, "event", row.eventId),
                    sourceId: row.eventId,
                    clientEpisodeId: row.clientEpisodeId,
                    kind: "event",
                    reasonCode: rejection.reasonCode || "rejected",
                    quarantinedAtMs: Date.now(),
                    item: row.envelope,
                });
                await this.db.events.delete(row.eventId);
            }
        });
    }

    async markEventsRetry(
        ownerUid: string,
        eventIds: string[],
        nextAttemptAtMs: number,
        errorCode: string,
    ): Promise<void> {
        if (eventIds.length === 0) return;
        await this.db.transaction("rw", this.db.events, async () => {
            for (const eventId of eventIds) {
                const row = await this.db.events.get(eventId);
                if (!row || row.ownerUid !== ownerUid) continue;
                await this.db.events.update(eventId, {
                    nextAttemptAtMs,
                    attemptCount: row.attemptCount + 1,
                    lastErrorCode: errorCode,
                });
            }
        });
    }

    async getReadyCheckpoints(
        ownerUid: string,
        clientEpisodeId: string,
        limit: number,
        nowMs: number,
    ): Promise<StoredContributionCheckpoint[]> {
        if (!String(ownerUid ?? "").trim()) return [];
        const rows = await this.db.checkpoints
            .where("[ownerUid+clientEpisodeId]")
            .equals([ownerUid, clientEpisodeId])
            .toArray();
        return rows
            .filter((row) => row.nextAttemptAtMs <= nowMs)
            .sort((a, b) => a.createdAtMs - b.createdAtMs)
            .slice(0, Math.max(1, limit));
    }

    async removeCheckpoints(ownerUid: string, checkpointIds: string[]): Promise<void> {
        if (checkpointIds.length === 0) return;
        await this.db.transaction("rw", this.db.checkpoints, async () => {
            for (const checkpointId of checkpointIds) {
                const row = await this.db.checkpoints.get(checkpointId);
                if (row?.ownerUid === ownerUid) await this.db.checkpoints.delete(checkpointId);
            }
        });
    }

    async quarantineCheckpoints(
        ownerUid: string,
        rejections: Array<{ id: string; reasonCode: string }>,
    ): Promise<void> {
        if (rejections.length === 0) return;
        await this.db.transaction("rw", this.db.checkpoints, this.db.quarantine, async () => {
            for (const rejection of rejections) {
                const row = await this.db.checkpoints.get(rejection.id);
                if (!row || row.ownerUid !== ownerUid) continue;
                await this.db.quarantine.put({
                    ownerUid,
                    quarantineId: this.quarantineId(ownerUid, "checkpoint", row.checkpointId),
                    sourceId: row.checkpointId,
                    clientEpisodeId: row.clientEpisodeId,
                    kind: "checkpoint",
                    reasonCode: rejection.reasonCode || "rejected",
                    quarantinedAtMs: Date.now(),
                    item: row.checkpoint,
                });
                await this.db.checkpoints.delete(row.checkpointId);
            }
        });
    }

    async markCheckpointsRetry(
        ownerUid: string,
        checkpointIds: string[],
        nextAttemptAtMs: number,
        errorCode: string,
    ): Promise<void> {
        if (checkpointIds.length === 0) return;
        await this.db.transaction("rw", this.db.checkpoints, async () => {
            for (const checkpointId of checkpointIds) {
                const row = await this.db.checkpoints.get(checkpointId);
                if (!row || row.ownerUid !== ownerUid) continue;
                await this.db.checkpoints.update(checkpointId, {
                    nextAttemptAtMs,
                    attemptCount: row.attemptCount + 1,
                    lastErrorCode: errorCode,
                });
            }
        });
    }

    async getPendingCounts(ownerUid: string, clientEpisodeId: string): Promise<ContributionPendingCounts> {
        if (!String(ownerUid ?? "").trim()) return { events: 0, checkpoints: 0 };
        const [events, checkpoints] = await Promise.all([
            this.db.events.where("[ownerUid+clientEpisodeId]").equals([ownerUid, clientEpisodeId]).count(),
            this.db.checkpoints.where("[ownerUid+clientEpisodeId]").equals([ownerUid, clientEpisodeId]).count(),
        ]);
        return { events, checkpoints };
    }

    async markEpisodeCompleted(ownerUid: string, clientEpisodeId: string): Promise<void> {
        await this.db.transaction("rw", this.db.episodes, async () => {
            const episode = await this.requireOwnedEpisode(ownerUid, clientEpisodeId);
            if (episode.status !== "completing") return;
            await this.db.episodes.update(clientEpisodeId, {
                status: "completed",
                updatedAtMs: Date.now(),
                nextAttemptAtMs: 0,
                attemptCount: 0,
            });
        });
    }

    async getQueueDepth(ownerUid: string): Promise<number> {
        if (!String(ownerUid ?? "").trim()) return 0;
        return this.ownerQueueDepth(ownerUid);
    }

    async getQuarantineCount(ownerUid: string): Promise<number> {
        if (!String(ownerUid ?? "").trim()) return 0;
        return this.db.quarantine.where("ownerUid").equals(ownerUid).count();
    }
}
